import { TriangleAlert } from 'lucide-react';
import type React from 'react';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, FieldLabel } from '@/components/ui/field';
import InputWithEndButtons from '@/components/ui/input-with-end-buttons';
import { isUnmodifiedEnter } from '@/features/Quote/hooks/useConfirmOnEnter';
import { getCurrentAbsenceBaseYear } from '@/services/utils';
import {
	formatAbsencePercentageOnBlur,
	validateAbsencePercentageInput
} from '@/services/validators';

interface AbsenceValues {
	vz1?: string;
	vz2?: string;
	vz3?: string;
}

interface AbsenceInputProps {
	index: string;
	absenceValues: AbsenceValues;
	/** Edit handlers are optional: a disabled input never fires them. */
	handleAbsenceFieldChange?: (field: keyof AbsenceValues, value: string) => void;
	handleAbsenceFieldBlur?: (field: keyof AbsenceValues, value: string) => void;
	handleAbsenceFieldInput?: (e: React.FormEvent<HTMLInputElement>) => void;
	inputRef?: RefObject<HTMLInputElement | null>;
	onEnterKey?: () => void;
	disabled?: boolean;
	/**
	 * Id of the sector-average hint when this year is pre-filled with the sector average;
	 * the field is then flagged and described by that hint.
	 */
	sectorAverageHintId?: string;
}

const AbsenceInput = ({
	index,
	absenceValues,
	handleAbsenceFieldChange,
	handleAbsenceFieldBlur,
	handleAbsenceFieldInput,
	inputRef,
	onEnterKey,
	disabled = false,
	sectorAverageHintId
}: AbsenceInputProps): React.JSX.Element => {
	const { t } = useTranslation();
	const fieldName = `vz${index}` as keyof AbsenceValues;

	// Get the raw value from state (don't format during typing)
	const displayValue = absenceValues?.[fieldName] ?? '';

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
		if (!isUnmodifiedEnter(e)) return;
		e.preventDefault();
		onEnterKey?.();
	};

	return (
		<Field key={`vz${index}Input`}>
			<FieldLabel htmlFor={`vz${index}Input`} className="font-bold items-center gap-1.5">
				{getCurrentAbsenceBaseYear() - Number(index)}
				{sectorAverageHintId && (
					<TriangleAlert
						role="img"
						aria-label={t('quotes.settings.absence.sectorAverageLabel')}
						className="size-3.5 shrink-0 text-yellow-500"
						data-testid={`absence-sector-average-icon-${index}`}
					/>
				)}
			</FieldLabel>
			<InputWithEndButtons
				id={`vz${index}Input`}
				testId={`absence-input-${index}`}
				value={displayValue}
				minValue={0}
				maxValue={99.9}
				step={0.1}
				className="w-20"
				groupClassName={sectorAverageHintId ? 'border-yellow-500' : undefined}
				ariaDescribedBy={sectorAverageHintId}
				inputClassName="text-lg sm:text-sm text-left"
				type="text"
				inputMode="decimal"
				pattern="[0-9]*[.,]?[0-9]*"
				validateOnChange={validateAbsencePercentageInput}
				disabled={disabled}
				onChange={(value) => {
					if (validateAbsencePercentageInput(value)) {
						handleAbsenceFieldChange?.(fieldName, value);
					}
				}}
				onBlur={(value) => {
					const formatted = formatAbsencePercentageOnBlur(value);
					handleAbsenceFieldBlur?.(fieldName, formatted);
				}}
				onInput={handleAbsenceFieldInput}
				onKeyDown={onEnterKey ? handleKeyDown : undefined}
				inputRef={inputRef}
			/>
		</Field>
	);
};
export default AbsenceInput;
